/** Totale inviti dall'API. null se il campo non c'è o non è un numero > 0. */
export const readInviteTotal = (value) => {
  if (value == null || value === '') return null;
  const total = Number(value);
  if (!Number.isFinite(total) || total <= 0) return null;
  return total;
};

const storageKey = (salonId) => `zbInviteJourney:${salonId}`;

const readStored = (salonId) => {
  if (!salonId || typeof localStorage === 'undefined') return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(salonId)) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStored = (salonId, value) => {
  if (!salonId || typeof localStorage === 'undefined') return;
  localStorage.setItem(storageKey(salonId), JSON.stringify(value));
};

export const buildInviteStages = (total) => {
  const cap = Math.max(0, Number(total) || 0);
  if (cap === 0) return [];

  // Un solo step finale: tutto il percorso verso il totale invitabile.
  return [
    {
      index: 0,
      mark: 100,
      cumulative: cap,
      size: cap,
    },
  ];
};

/**
 * Fascia titolo in base agli inviti già inviati (non più legata a step multipli).
 * start → community (≥10 o ≥10%) → group (≥35%) → app (≥75%).
 */
export const getInviteTitleTier = (spent, baseline) => {
  const cap = Math.max(0, Number(baseline) || 0);
  const sent = Math.max(0, Number(spent) || 0);
  if (!cap) return 'start';
  if (sent < Math.min(10, cap)) return 'start';
  const pct = (sent / cap) * 100;
  if (pct < 35) return 'community';
  if (pct < 75) return 'group';
  return 'app';
};

const clampProgress = (baseline, spent, serverRemaining, configuredTotal) => {
  const total = readInviteTotal(configuredTotal);
  let nextBaseline = baseline;
  let nextSpent = Math.max(0, Number(spent) || 0);
  const remainingRaw = Number(serverRemaining);
  const remaining = Number.isFinite(remainingRaw) ? Math.max(0, remainingRaw) : 0;
  // 0 all'avvio è ambiguo (Redux default vs quota finita): non usarlo per init/complete.
  const hasPositiveRemaining = remaining > 0;

  if (total > 0) {
    nextBaseline = total;
    if (hasPositiveRemaining) nextSpent = Math.max(0, nextBaseline - remaining);
    if (nextSpent > nextBaseline) nextSpent = nextBaseline;
    return {
      baseline: nextBaseline,
      spent: nextSpent,
      remaining: nextBaseline - nextSpent,
    };
  }

  if (!Number.isFinite(nextBaseline) || nextBaseline <= 0) {
    if (!hasPositiveRemaining) {
      return { baseline: 0, spent: 0, remaining: 0, pending: true };
    }
    nextBaseline = remaining;
    nextSpent = 0;
    return { baseline: nextBaseline, spent: nextSpent, remaining: nextBaseline - nextSpent };
  }

  if (hasPositiveRemaining) {
    // Senza totale configurato: tieni il massimo baseline visto.
    // spent = baseline − remaining (fonte server).
    if (remaining > nextBaseline) nextBaseline = remaining;
    nextSpent = Math.max(0, nextBaseline - remaining);
  }
  // remaining === 0: non alzare spent a baseline (evita false "missione compiuta").
  // Lo spent locale resta.

  if (nextSpent > nextBaseline) nextSpent = nextBaseline;

  return {
    baseline: nextBaseline,
    spent: nextSpent,
    remaining: nextBaseline - nextSpent,
  };
};

export const syncInviteJourney = (salonId, serverRemaining, configuredTotal) => {
  const stored = readStored(salonId);
  const progress = clampProgress(
    Number(stored?.baseline),
    Number(stored?.spent) || 0,
    serverRemaining,
    configuredTotal,
  );
  // Non persistere uno stato vuoto spurio prima che arrivi un remaining > 0.
  if (salonId && !progress.pending) {
    writeStored(salonId, { baseline: progress.baseline, spent: progress.spent });
  }
  return describeJourney(progress);
};

export const addInviteSpent = (salonId, count, serverRemaining, configuredTotal) => {
  const current = syncInviteJourney(salonId, serverRemaining, configuredTotal);
  const baseline = Number.isFinite(Number(current?.baseline)) ? Math.max(0, Number(current.baseline)) : 0;
  const spent = Math.min(baseline, (Number(current?.spent) || 0) + Math.max(0, Number(count) || 0));
  writeStored(salonId, { baseline, spent });
  return describeJourney({
    baseline,
    spent,
    remaining: baseline - spent,
  });
};

export const describeJourney = ({ baseline, spent, remaining }) => {
  const cap = Number.isFinite(Number(baseline)) ? Math.max(0, Number(baseline)) : 0;
  const sentRaw = Number.isFinite(Number(spent)) ? Math.max(0, Number(spent)) : 0;
  const leftRaw = Number.isFinite(Number(remaining)) ? Number(remaining) : cap - sentRaw;
  const stages = buildInviteStages(cap);
  const sent = Math.min(sentRaw, cap);
  const current = stages.find((stage) => sent < stage.cumulative) || null;
  const progressInStage = current ? sent - (current.cumulative - current.size) : 0;
  const next = current ? stages[current.index + 1] || null : null;
  return {
    baseline: cap,
    spent: sent,
    remaining: Math.max(0, leftRaw),
    stages,
    current,
    next,
    progressInStage,
    filled: current ? Math.min(sent, current.cumulative) : sent,
    goal: current ? current.cumulative : cap,
    done: !current,
  };
};

export const shouldShowInvitePromo = (journey) => {
  if (!journey?.current || journey.remaining <= 0) return false;
  // Limite clienti totali (> 20) sospeso: da ripristinare insieme al secondo argomento.
  return true;
};
